import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import AuthCard from '@/components/AuthCard'
import SocialSignIn from '@/components/SocialSignIn'
import { Button } from '@/components/ui/button'
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import { capture } from '@/lib/analytics/posthog'
import { authClient, signIn } from '@/lib/auth-client'
import { getEnabledSocialProviders } from '@/lib/auth-social'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    // Only same-origin relative paths: a single leading slash, not "//" or "/\"
    // (which browsers treat as protocol-relative → open redirect). Bounded too.
    const r = search.redirect
    return typeof r === 'string' && r.length <= 512 && /^\/(?![/\\])/.test(r)
      ? { redirect: r }
      : {}
  },
  head: () => ({
    // noindex: an auth form has no organic-search value and we don't want it
    // competing with/duplicating the marketing pages in results.
    meta: pageMeta({
      title: 'Sign in · Masklabs',
      description: 'Sign in to your Masklabs account.',
      path: '/login',
      noindex: true,
    }),
    links: pageLinks('/login'),
  }),
  loader: async () => ({
    socialProviders: await getEnabledSocialProviders(),
  }),
  component: LoginPage,
})

type ResendState = 'idle' | 'sending' | 'sent' | 'rate-limited' | 'error'

function LoginPage() {
  const { redirect } = Route.useSearch()
  const { socialProviders } = Route.useLoaderData()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  // Set when sign-in is rejected specifically for an unverified email, so we
  // can offer a resend instead of the generic error message.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [resendState, setResendState] = useState<ResendState>('idle')
  const { remaining, start } = useResendCooldown()

  const target = redirect?.startsWith('/') ? redirect : '/dashboard'

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setUnverifiedEmail(null)
    setResendState('idle')
    setPending(true)
    const { error: signInError } = await signIn.email({
      email,
      password,
      callbackURL: `${window.location.origin}${target}`,
    })
    setPending(false)
    if (signInError) {
      if (signInError.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(email)
        return
      }
      setError(signInError.message ?? 'Sign in failed.')
      return
    }
    capture('login')
    // Full navigation so SSR picks up the freshly-set session cookie.
    window.location.href = target
  }

  async function onResend() {
    if (!unverifiedEmail) return
    setResendState('sending')
    const { error: resendError } = await authClient.sendVerificationEmail({
      email: unverifiedEmail,
      callbackURL: `${window.location.origin}${target}`,
    })
    if (!resendError) {
      setResendState('sent')
      start()
      return
    }
    setResendState(resendError.status === 429 ? 'rate-limited' : 'error')
  }

  return (
    <AuthCard>
      <CardHeader>
        <CardTitle className="text-2xl">Sign in</CardTitle>
        <CardDescription>
          Manage your credentials, usage, and balance.
        </CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        <CardContent>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {unverifiedEmail ? (
              <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/50 p-3">
                <p className="text-sm text-foreground">
                  Please verify your email before signing in.
                </p>
                {resendState === 'sent' ? (
                  <p className="text-sm text-muted-foreground">
                    Verification email sent. Check your inbox.
                  </p>
                ) : resendState === 'rate-limited' ? (
                  <p className="text-sm text-destructive">
                    You've requested a few links already. Please wait a bit
                    before trying again.
                  </p>
                ) : resendState === 'error' ? (
                  <p className="text-sm text-destructive">
                    Couldn't resend the verification email. Please try again in
                    a moment.
                  </p>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={resendState === 'sending' || remaining > 0}
                  onClick={onResend}
                >
                  {resendState === 'sending'
                    ? 'Sending…'
                    : remaining > 0
                      ? `Resend in ${remaining}s`
                      : 'Resend verification email'}
                </Button>
              </div>
            ) : null}
          </FieldGroup>
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>
          <SocialSignIn providers={socialProviders} callbackPath={target} />
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/forgot-password"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </p>
          <p className="text-center text-sm text-muted-foreground">
            No account?{' '}
            <Link
              to="/signup"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Create one
            </Link>
          </p>
        </CardFooter>
      </form>
    </AuthCard>
  )
}
